import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import {
    type AllowedPartners,
    TemplateStatus,
    useExpertUniversityConfiguration,
    useExpertUniversityStore,
} from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    Accordion,
    Anchor,
    Button,
    Container,
    Group,
    LoadingOverlay,
    Overlay,
    ScrollArea,
    SimpleGrid,
    Stack,
    Switch,
    Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { type FC, useEffect, useMemo, useState } from "react";

import { Warning } from "@/common-ui";
import { LoadingAnimation } from "@/common-ui/icons/sales";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useFeatureFlag, userCache } from "@/shared-utils";

import type { TrainingSession, TrainingSessionEvaluationResults } from "../../types";

import { useEmployeeTrainingSessions } from "../../hooks/useEmployeeTrainingSessions";
import { useTrainingEvaluationResults } from "../../hooks/useTrainingEvaluationResults";
import { useTrainingProfiles } from "../../hooks/useTrainingProfiles";
import { groupProfilesByLearningBlock, isEvaluationTranscriptContent } from "../../utils";
import classes from "../ExpertUniversityHome.module.css";
import { LaunchCallSandboxButton } from "../LaunchCallSandboxButton";
import { Sidebar } from "../Sidebar";
import { TrainingProfileTile } from "../TrainingProfileTile";
import AscendEvaluationPage from "./AscendEvaluationPage";
import EvaluationResultsOverlay from "./EvaluationResultsOverlay";
import styles from "./ExpertUniversityLearningBlocks.module.css";

const PROFILE_TESTING_GROUP = "HQDOMAIN_APP-NON-VZW-APP-SOLUTO-TRAINING-ASSIST-";

export const ExpertUniversityLearningBlocks: FC = () => {
    const logger = useLogger({ module: "Expert University Learning Blocks" });

    const { trainingPartner: partner, setTaskInSandbox } = useExpertUniversityConfiguration();
    const { eventBus: expertUniversitySdkEventBus } = useExpertUniversityStore.getState();

    // Evaluation-results flow (ASCEND pilot only — this component is the pilot's entry point).
    const { evaluationResults, isPending, clearEvaluationResults, viewResultsBySessionId } =
        useTrainingEvaluationResults();

    // Feature flags
    const { data: isExpertUniversityAdminUser } = useFeatureFlag<boolean>("EWP-isExpertUniversityAdminUser");

    const shouldShowInProgressProfiles =
        userCache.groups.includes(PROFILE_TESTING_GROUP) || isExpertUniversityAdminUser;
    // `undefined` means "not yet touched" so the derived value tracks the async admin flag until the
    // expert explicitly toggles the Switch; capturing the initial flag value would freeze in a wrong
    // default for admin-only users (the flag is undefined until it resolves).
    const [showInProgressOverride, setShowInProgressOverride] = useState<boolean | undefined>(undefined);
    const showInProgressProfiles = showInProgressOverride ?? shouldShowInProgressProfiles;
    const [isStartingTrainingSession, setIsStartingTrainingSession] = useState(false);
    // `undefined` means "not yet touched" → default to all blocks open without an effect/flash.
    const [openBlocks, setOpenBlocks] = useState<string[] | undefined>(undefined);
    // "View results" opens a summary overlay for the selected profile's last session; from there the
    // expert can jump to the full transcript (AscendEvaluationPage) or go back to the accordion.
    const [resultsOverlay, setResultsOverlay] = useState<{ session: TrainingSession; title: string } | undefined>(
        undefined,
    );

    const correlationId = crypto.randomUUID();
    const partnerTrainingChallengeProfiles = useTrainingProfiles("asurion", partner as AllowedPartners, correlationId);

    // Fetch the expert's sessions once, keyed stably (no eval-driven refetch in this iteration).
    const { data: expertTrainingSessions } = useEmployeeTrainingSessions(
        `home-${partner}`,
        userCache.employeeId,
        partner as AllowedPartners,
    );

    // Clear any stale reconnect flag on mount so an earlier abnormal websocket close does not
    // cause a fresh pilot session to be misclassified as a reconnect attempt (Home does this too).
    useEffect(() => {
        useExpertUniversityStore.getState().setReconnect(false);
    }, []);

    // Start-of-session feedback lifecycle. Registered once per event bus with proper cleanup,
    // improving on Home which re-registers these listeners on every render.
    useEffect(() => {
        if (!expertUniversitySdkEventBus) {
            return;
        }
        const offStarting = expertUniversitySdkEventBus.on("starting-training-session", () => {
            setIsStartingTrainingSession(true);
        });
        const offStarted = expertUniversitySdkEventBus.on("training-session-started", () => {
            setIsStartingTrainingSession(false);
        });
        const offFailed = expertUniversitySdkEventBus.on("failed-to-start-training-session", () => {
            setIsStartingTrainingSession(false);
            showNotification({
                id: "start-training-session-error",
                position: "top-center",
                withCloseButton: true,
                autoClose: 3000,
                title: "Unable to start training Session",
                message: "There was a problem starting the training session. Please try again.",
                color: "red",
                loading: false,
            });
        });
        return () => {
            offStarting();
            offStarted();
            offFailed();
        };
    }, [expertUniversitySdkEventBus]);

    const filtered = showInProgressProfiles
        ? partnerTrainingChallengeProfiles
        : partnerTrainingChallengeProfiles?.filter((profile) => profile.status === TemplateStatus.IN_PRODUCTION);

    const groups = useMemo(() => groupProfilesByLearningBlock(filtered), [filtered]);

    // The "Unassigned" block only appears while in-progress profiles are shown; otherwise it is hidden.
    const visibleGroups = useMemo(
        () => (showInProgressProfiles ? groups : groups.filter((group) => !group.isUnassigned)),
        [groups, showInProgressProfiles],
    );
    // "Expand all" opens every currently-visible block.
    const visibleKeys = useMemo(() => visibleGroups.map((group) => group.key), [visibleGroups]);
    // Default-open set: every visible block except "Unassigned", which starts collapsed.
    const defaultOpenKeys = useMemo(
        () => visibleGroups.filter((group) => !group.isUnassigned).map((group) => group.key),
        [visibleGroups],
    );

    // Default to the default-open set until the expert explicitly changes the selection.
    const value = openBlocks ?? defaultOpenKeys;

    // Branch order mirrors ExpertUniversityHome: loading -> results -> error -> normal view.
    if (isPending || isStartingTrainingSession) {
        return (
            <LoadingOverlay
                visible
                loaderProps={{
                    children: (
                        <Container maw="50%">
                            <LoadingAnimation />
                            <Text>
                                {isPending
                                    ? "Loading results...it may take up to 20 seconds."
                                    : "Starting training session...it may take up to 30 seconds."}
                            </Text>
                        </Container>
                    ),
                }}
                overlayProps={{
                    backgroundOpacity: 1,
                    color: "var(--exwo-color-app-bg)",
                }}
            />
        );
    }

    if (evaluationResults && isEvaluationTranscriptContent(evaluationResults)) {
        return (
            <div className={classes.background}>
                <AscendEvaluationPage
                    evaluationResults={evaluationResults}
                    onCompleteEvaluation={() => {
                        clearEvaluationResults();
                        setResultsOverlay(undefined);
                    }}
                />
            </div>
        );
    }

    if (evaluationResults?.ErrorCode) {
        return (
            <Overlay data-testid="return-home-screen" color="var(--exwo-color-app-bg)" backgroundOpacity={0.85}>
                <Stack align="center" justify="center" h="100%" gap="1.5rem" className={sharedStyles.animateScaleIn}>
                    <Warning width={80} height={80} />
                    <Text fw="700" ta="center" size="xl">
                        There was an issue evaluating your results, please select a profile and try again.
                    </Text>
                    <Button onClick={clearEvaluationResults}>Return Home</Button>
                </Stack>
            </Overlay>
        );
    }

    if (resultsOverlay) {
        return (
            <EvaluationResultsOverlay
                title={resultsOverlay.title}
                session={resultsOverlay.session}
                onBack={() => setResultsOverlay(undefined)}
                onViewTranscript={() => {
                    const sessionId = resultsOverlay.session.evaluationDetails[0]?.SessionId;
                    if (sessionId) {
                        void viewResultsBySessionId(sessionId);
                    }
                }}
            />
        );
    }

    return (
        <div className={classes.background}>
            <div className={classes.container}>
                <div className={classes.expertUniversityColumn}>
                    <Stack gap="1.5rem" h="100%">
                        <Group gap="4">
                            <Text
                                fw={700}
                                c="var(--mantine-color-dark-3)"
                                fz="2rem"
                                lh="1.5rem"
                                className={styles.title}
                            >
                                Select a Profile
                            </Text>
                            <LaunchCallSandboxButton />
                        </Group>
                        <Group align="center" gap="md">
                            {shouldShowInProgressProfiles && (
                                <Switch
                                    label="Show in progress profiles"
                                    checked={showInProgressProfiles}
                                    onChange={(event) => setShowInProgressOverride(event.currentTarget.checked)}
                                    fw={700}
                                    pl="0.5rem"
                                    c="var(--mantine-color-dark-3)"
                                />
                            )}
                            <Anchor
                                component="button"
                                type="button"
                                ml="auto"
                                fw={700}
                                c="var(--mantine-color-success-4)"
                                onClick={() => setOpenBlocks(value.length > 0 ? [] : visibleKeys)}
                            >
                                {value.length > 0 ? "Collapse all" : "Expand all"}
                            </Anchor>
                        </Group>
                        <ScrollArea offsetScrollbars scrollbarSize={8} classNames={{ viewport: styles.scrollViewport }}>
                            <Accordion
                                multiple
                                chevronPosition="right"
                                value={value}
                                onChange={setOpenBlocks}
                                classNames={{
                                    item: styles.accordionItem,
                                    chevron: styles.accordionChevron,
                                    content: styles.accordionContent,
                                    control: styles.accordionControl,
                                    label: styles.accordionLabel,
                                }}
                            >
                                {visibleGroups.map((group) => (
                                    <Accordion.Item key={group.key} value={group.key}>
                                        <Accordion.Control>{group.label}</Accordion.Control>
                                        <Accordion.Panel>
                                            {group.profiles.length ? (
                                                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                                    {group.profiles.map((profile) => {
                                                        const trainingProfileSessions = expertTrainingSessions?.find(
                                                            (session) => session.challengeId === profile.id,
                                                        );

                                                        // Default to the first session; if the expert has taken the
                                                        // challenge more than once, use the most recent by timestamp.
                                                        let latestSession:
                                                            | TrainingSessionEvaluationResults
                                                            | undefined =
                                                            trainingProfileSessions?.evaluationDetails?.[0];
                                                        if (
                                                            trainingProfileSessions?.challengeTaken &&
                                                            trainingProfileSessions.evaluationDetails.length > 1
                                                        ) {
                                                            latestSession =
                                                                trainingProfileSessions.evaluationDetails.reduce(
                                                                    (a, b) => {
                                                                        const previousSessionTime = a.Timestamp ?? 0;
                                                                        const currentSessionTime = b.Timestamp ?? 0;
                                                                        return new Date(previousSessionTime) >
                                                                            new Date(currentSessionTime)
                                                                            ? a
                                                                            : b;
                                                                    },
                                                                );
                                                        }

                                                        return (
                                                            <TrainingProfileTile
                                                                key={profile.id}
                                                                id={profile.id}
                                                                description={profile.description}
                                                                challengeTaken={
                                                                    trainingProfileSessions?.challengeTaken ?? false
                                                                }
                                                                challengeStatus={latestSession?.Results}
                                                                onStart={async () => {
                                                                    const isInLiveKitPilot = await getFeature<boolean>(
                                                                        "EWP-isInLiveKitPilot",
                                                                        {
                                                                            profile: profile.id,
                                                                            partner: partner as AllowedPartners,
                                                                        },
                                                                    );
                                                                    trackFullStoryEvent(
                                                                        "Expert_University_Training_Session_Started",
                                                                        { profileId: profile.id },
                                                                    );
                                                                    if (expertUniversitySdkEventBus) {
                                                                        logger.info(
                                                                            { profileId: profile.id },
                                                                            "CALL CREATE TRAINING TASK",
                                                                        );
                                                                        setTaskInSandbox(false);
                                                                        expertUniversitySdkEventBus.emit(
                                                                            "start-simulation-training",
                                                                            {
                                                                                profileId: profile.id,
                                                                                partner: partner as AllowedPartners,
                                                                                isLiveKitSession: isInLiveKitPilot,
                                                                            },
                                                                        );
                                                                    }
                                                                }}
                                                                onViewResults={async () => {
                                                                    if (trainingProfileSessions) {
                                                                        setResultsOverlay({
                                                                            session: trainingProfileSessions,
                                                                            title: profile.description,
                                                                        });
                                                                    }
                                                                    trackFullStoryEvent(
                                                                        "Expert_University_View_Results_Called",
                                                                        {
                                                                            sessionId: latestSession?.SessionId,
                                                                            profileId: profile.id,
                                                                        },
                                                                    );
                                                                    logger.info(
                                                                        {
                                                                            profileId: profile.id,
                                                                            sessionId: latestSession?.SessionId,
                                                                        },
                                                                        "CALL VIEW RESULTS",
                                                                    );
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </SimpleGrid>
                                            ) : (
                                                <Text c="dimmed" size="sm">
                                                    No profiles yet
                                                </Text>
                                            )}
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </ScrollArea>
                    </Stack>
                </div>
                <div className={classes.expertUniversityColumn}>
                    <Sidebar partner={partner as AllowedPartners} />
                </div>
            </div>
        </div>
    );
};

export default ExpertUniversityLearningBlocks;
